/** Scheduled vs projected session end times. */

import type { SessionTopicPlan } from "./types";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Parse "HH:MM", "H:MM", or "h:mm AM/PM" → minutes from midnight. */
export function parseFlexibleTimeToMinutes(timeLabel?: string): number | null {
  if (!timeLabel) return null;
  const raw = String(timeLabel).trim();
  const ampm = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let hours = Number.parseInt(ampm[1], 10);
    const minutes = Number.parseInt(ampm[2], 10);
    const meridiem = ampm[3].toUpperCase();
    if (meridiem === "PM" && hours !== 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;
    return hours * 60 + minutes;
  }
  const hm = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hm) {
    return Number.parseInt(hm[1], 10) * 60 + Number.parseInt(hm[2], 10);
  }
  return null;
}

export function parseEndTimeToMinutes(timeLabel: string): number | null {
  return parseFlexibleTimeToMinutes(timeLabel);
}

/** Sum of planned topic minutes. */
export function plannedSessionMinutes(topics: SessionTopicPlan[]): number {
  return topics.reduce((sum, t) => sum + Math.max(0, t.durationMin || 0), 0);
}

/** Wall-clock end label from start + duration minutes (12h). */
export function computeExpectedEndTime(
  startTime: string,
  durationMinutes: number,
): string {
  return formatWallClockFromStart(startTime, Math.max(0, durationMinutes) * 60);
}

export function computeScheduledEndTime(
  startTime: string,
  durationMinutes: number,
): string {
  return computeExpectedEndTime(startTime, durationMinutes);
}

/** Format wall clock from session start + offset seconds (12h). */
export function formatWallClockFromStart(
  startTime: string,
  offsetSeconds: number,
): string {
  const startMin = parseFlexibleTimeToMinutes(startTime);
  if (startMin === null) return "";
  const totalSec = startMin * 60 + Math.max(0, Math.floor(offsetSeconds));
  const endH = Math.floor(totalSec / 3600) % 24;
  const endM = Math.floor((totalSec % 3600) / 60);
  const ampm = endH >= 12 ? "PM" : "AM";
  const h12 = endH % 12 || 12;
  return `${h12}:${pad2(endM)} ${ampm}`;
}

/** Format instant as IST wall clock (matches Scheduled End / session times). */
function formatWallClockFromDate(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Remaining topic work (seconds):
 * - current (started): (planned + bonus) - elapsed — may be negative when overrun
 * - current (not started): planned + bonus
 * - pending: planned
 * - completed / skipped: 0 (used time already in wall-clock now; unused planned dropped)
 *
 * Negative current remaining freezes Expected End once planned+bonus is used up,
 * so the same +10m extension is not re-applied every second. Trainer must click
 * "Need more time" again to push Expected End out further.
 */
export function computeRemainingTopicWorkSeconds(
  topics: SessionTopicPlan[],
  currentTopic: SessionTopicPlan | undefined,
  topicElapsedSec: number,
  topicTimeBonusSec: number,
  topicStarted = false,
): number {
  if (topics.length === 0) return 0;
  const current = currentTopic ?? topics.find((topic) => !topic.completed);
  let remaining = 0;

  for (const topic of topics) {
    if (topic.completed) continue; // taught or skipped — unused planned removed
    const plannedSec = Math.max(0, topic.durationMin) * 60;
    if (current && topic.id === current.id) {
      const limit = plannedSec + Math.max(0, topicTimeBonusSec);
      remaining += topicStarted
        ? limit - Math.max(0, topicElapsedSec)
        : limit;
    } else {
      remaining += plannedSec;
    }
  }
  return remaining;
}

/**
 * Live Expected End:
 *   now + current topic remaining + pending planned + remaining Q&A
 *
 * Past pauses are already reflected in wall-clock "now".
 * Active Q&A remaining pushes Expected End out; on resume it recalculates.
 */
export function computeProjectedSessionEndTime(
  _startTime: string,
  topics: SessionTopicPlan[],
  currentTopic: SessionTopicPlan | undefined,
  topicElapsedSec: number,
  topicTimeBonusSec: number,
  _pauseAccumulatedSec = 0,
  topicStarted = false,
  qaRemainingSec = 0,
  nowMs = Date.now(),
): string {
  const remainingTopics = computeRemainingTopicWorkSeconds(
    topics,
    currentTopic,
    topicElapsedSec,
    topicTimeBonusSec,
    topicStarted,
  );
  // Overrun makes remainingTopics negative; never push Expected End into the past.
  const totalRemaining =
    Math.max(0, remainingTopics) + Math.max(0, Math.floor(qaRemainingSec));
  return formatWallClockFromDate(new Date(nowMs + totalRemaining * 1000));
}

/** Alias kept for callers that want an explicit now-based helper. */
export function computeProjectedEndFromNow(
  topics: SessionTopicPlan[],
  currentTopic: SessionTopicPlan | undefined,
  topicElapsedSec: number,
  topicTimeBonusSec: number,
  topicStarted = false,
  qaRemainingSec = 0,
  nowMs = Date.now(),
): string {
  return computeProjectedSessionEndTime(
    "",
    topics,
    currentTopic,
    topicElapsedSec,
    topicTimeBonusSec,
    0,
    topicStarted,
    qaRemainingSec,
    nowMs,
  );
}
